import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axios from 'axios';
import ConfirmationDialog from './ConfirmationDialog';

const UsersTableDialog = ({ open, onClose, serverUrl, token, onMessage, onError }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [userPendingDelete, setUserPendingDelete] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${serverUrl}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data || []);
    } catch (e) {
      console.error('Failed to fetch users', e);
      onError && onError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (id) => {
    try {
      await axios.delete(`${serverUrl}/admin/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onMessage && onMessage('User deleted');
      fetchUsers();
    } catch (e) {
      console.error('Failed to delete user', e);
      onError && onError('Failed to delete user');
    }
  };

  const askDelete = (user) => {
    setUserPendingDelete(user);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userPendingDelete) return;
    const id = userPendingDelete.id;
    setConfirmOpen(false);
    setUserPendingDelete(null);
    await deleteUser(id);
  };

  useEffect(() => {
    if (open) fetchUsers();
  }, [open]);

  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'username', headerName: 'Username', flex: 1 },
    { field: 'role', headerName: 'Role', width: 140 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title={`Delete ${params.row.username}`}>
          <IconButton
            color="error"
            size="small"
            onClick={() => askDelete(params.row)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const rows = (users || []).map((u) => ({ id: u.id, username: u.username, role: u.role }));

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>👥 Users</DialogTitle>
      <DialogContent>
        <Box sx={{ height: 420, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            density="compact"
            disableRowSelectionOnClick
            loading={loading}
            pageSizeOptions={[5, 10, 20]}
            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
    <ConfirmationDialog
      open={confirmOpen}
      onClose={() => { setConfirmOpen(false); setUserPendingDelete(null); }}
      onConfirm={handleConfirmDelete}
      title="Confirm deletion"
      message={userPendingDelete ? `Are you sure you want to delete user "${userPendingDelete.username}"? This action cannot be undone.` : ''}
    />
    </>
  );
};

export default UsersTableDialog;
